import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { Pencil } from 'lucide-react';
import styles from './EditableReportTitleHeading.module.scss';

export type EditableReportTitleHeadingProps = {
  /** Semantic tag for the rendered title */
  as?: 'h1' | 'strong';
  /** Typography from the report page (e.g. CSS module `styles.reportTitle`) */
  titleClassName: string;
  resolvedTitle: string;
  value: string;
  defaultTitle: string;
  persistedTitle: string;
  onChange: (value: string) => void;
  onSave: () => void | Promise<void>;
  canSave: boolean;
  saving: boolean;
  /** When false, title is read-only (no edit affordance) */
  editable: boolean;
};

export function EditableReportTitleHeading({
  as = 'h1',
  titleClassName,
  resolvedTitle,
  value,
  defaultTitle,
  persistedTitle,
  onChange,
  onSave,
  canSave,
  saving,
  editable,
}: EditableReportTitleHeadingProps) {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipBlurSave = useRef(false);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const beginEdit = useCallback(() => {
    if (!editable || saving) return;
    setIsEditing(true);
  }, [editable, saving]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      skipBlurSave.current = true;
      void (async () => {
        if (canSave) {
          await onSave();
        }
        setIsEditing(false);
        skipBlurSave.current = false;
      })();
    },
    [canSave, onSave],
  );

  const handleBlur = useCallback(() => {
    if (skipBlurSave.current) return;
    void (async () => {
      if (canSave) {
        await onSave();
      }
      setIsEditing(false);
    })();
  }, [canSave, onSave]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onChange(persistedTitle);
        setIsEditing(false);
      }
    },
    [onChange, persistedTitle],
  );

  const Tag = as;

  if (!editable) {
    return <Tag className={titleClassName}>{resolvedTitle}</Tag>;
  }

  if (!isEditing) {
    return (
      <Tag className={titleClassName}>
        <span className={styles.viewWrap}>
          <button
            type="button"
            className={styles.titleButton}
            onClick={beginEdit}
          >
            {resolvedTitle}
          </button>
          <button
            type="button"
            className={styles.editIconFloating}
            aria-label="Edit report title"
            onClick={beginEdit}
          >
            <Pencil size={17} strokeWidth={2} />
          </button>
        </span>
      </Tag>
    );
  }

  return (
    <Tag className={titleClassName}>
      <form className={styles.editForm} onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          className={styles.editInput}
          value={value}
          placeholder={defaultTitle}
          aria-label="Report title"
          disabled={saving}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      </form>
    </Tag>
  );
}
